import { Box } from "@mui/material";
import HomeTableSkeleton from "src/components/common/HomeTableSkeleton";
import { AxiosError, AxiosResponse } from "axios";
import { useState } from "react";
import { useQuery } from "react-query";
import { useStateOptions } from "src/hooks/useStateOptions";
import {
  createSearchParams,
  Link as RouterLink,
  useNavigate,
} from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents/Buttons";
import { getProgressDetails } from "src/utils/api/dashboard";
import {
  dateAndTimeStamp,
  downloadSheet,
  formatINR,
  generateExportFilename,
} from "src/utils/helper";
import defaultStyles, { StylesClasses } from "./styles";
import { path } from "src/constants/path";
import SearchBar from "src/components/common/SearchField";
import MultiSelect from "src/components/common/MultiSelect";
import { getStyles } from "src/styles/theme";
import { useTableQuery } from "src/hooks/useTableQuery";
import { useRecordEntityOptions } from "src/hooks/useRecordEntityOptions";

const ProgressDetails = () => {
  const navigate = useNavigate();
  const [tableRows, setTableRows] = useState([]);

  const { states } = useStateOptions();
  const [totalCostEstimateReceived, setTotalCostEstimateReceived] = useState(0);
  const [totalProgressDetailReceived, setTotalProgressReceived] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const styles = getStyles<StylesClasses>(defaultStyles);

  const {
    queryParams,
    setQueryParams,
    paginationModel,
    filterKey,
    handleSearch,
    handleFilterChange,
    handleSort,
    handlePaginationChange,
    clampToTotal,
    searchDefaultValue,
  } = useTableQuery<{
    download: boolean;
    stateIds?: string[];
    landOwnershipStatus: string;
    targetStatus?: string;
    entityIds?: string[] | null;
  }>({
    pageKey: "progressDetails",
    extraParams: {
      download: false,
      stateIds: undefined,
      entityIds: undefined,
      landOwnershipStatus: "APPROVED",
      // targetStatus: "SUBMIT_TO_SNO",
    },
    defaultFilterKey: { stateIds: null, entityIds: null },
  });

  const { entityInfo } = useRecordEntityOptions("progress", {
    landOwnershipStatus: queryParams.landOwnershipStatus,
    targetStatus: queryParams.targetStatus,
    stateIds: queryParams.stateIds?.join(","),
    searchText: queryParams.searchText,
  });

  const { isLoading, isFetching } = useQuery(
    ["users", queryParams],
    () =>
      getProgressDetails({
        ...queryParams,
      }),
    {
      onSuccess: (res: AxiosResponse) => {
        const sortedData = res?.data?.data?.rows.sort((a, b) => {
          // Move objects with `isDigitalCostEstimateFilled: true` to the beginning
          return b.isDigitalCostEstimateFilled - a.isDigitalCostEstimateFilled;
        });
        const row = sortedData.map((row, idx) => ({
          // sno: row?.serialNo,
          sno: queryParams.offset + idx + 1,
          id: row?.id,
          costEstimateID: row.id,
          registrationNo: row.projectDetail.registration_no,
          entityName: row.user.firstName,
          state: row?.projectDetail?.address?.stateDetail?.name,
          division: row.projectDetail.landSpecification.division,
          allocateDivision:
            row.projectDetail.organizationAffiliationDetail?.userDetail
              ?.division,
          totalArea: `${row.netPlantationArea}`,
          ecoRestorationAmount: formatINR(row?.demandNote ?? "-"),
          created_at: dateAndTimeStamp(row.created_at, "IST"),
          updated_at: dateAndTimeStamp(row.updated_at, "IST"),
          isProgressFilled: row.isProgressFilled,
          isDigitalCostEstimateFilled: row.isDigitalCostEstimateFilled,
          totalFinancialProgress: row.costProgress
            ? Number(row.costProgress).toFixed(2)
            : "0.00",
          totalSeedlings: row?.totalNumberOfSeedlingToBePlanted,
          pendingQueryCount: row?.pendingQueryCount || 0,
          pendingSeedlingQueryCount: row?.pendingSeedlingQueryCount || 0,
        }));

        if (queryParams.download) {
          const exportData = row.map((item) => {
            return {
              "S.No": item?.sno,
              "Registration No": item?.registrationNo,
              "Entity Name": item.entityName,
              State: item?.state,
              "Allocate Division": item?.allocateDivision,
              Division: item?.division,
              "Net Planned Area (Ha)": item?.totalArea,
              "Pending Progress Queries": item?.pendingQueryCount,
              "Pending Seedling Queries": item?.pendingSeedlingQueryCount,
              "Eco-Restoration Cost (₹)": item?.ecoRestorationAmount,
              "Total Financial Progress (₹)": item?.totalFinancialProgress,
              "Total No. of Seedlings Proposed": item?.totalSeedlings,
              "Created At": item?.created_at,
              "Updated At": item?.updated_at,
            };
          });
          downloadSheet(exportData, generateExportFilename("progress-details"));
          setQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          const count = res?.data?.data?.count || 0;
          if (clampToTotal(count)) {
            return;
          }

          setTableRows(row);
          setTotalRecords(count);

          // Use totals directly from API response
          setTotalCostEstimateReceived(
            res?.data?.data?.totalDigitalCostEstimateReceived || 0
          );
          setTotalProgressReceived(
            res?.data?.data?.totalProgressDetailsReceived || 0
          );
        }
      },
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
    }
  );

  const RenderEPID = (params) => {
    return (
      <>
        <Box
          component={RouterLink}
          to={`${path.costEstimateDetails}?${createSearchParams({
            id: params?.row?.costEstimateID,
          })}`}
          sx={{ ...defaultStyles.renderButtonText3 }}
        >
          <Box
            component="img"
            src={
              params?.row?.isDigitalCostEstimateFilled
                ? "/assets/svg/details.svg"
                : "/assets/svg/details_red.svg"
            }
            sx={{ ...defaultStyles.imgStyle }}
          ></Box>
        </Box>
      </>
    );
  };

  const ViewProgressDetails = (params) => {
    return (
      <>
        <Box
          component={RouterLink}
          to={`${path.advanceWorkProgress}?${createSearchParams({
            id: params?.row?.costEstimateID,
          })}`}
          sx={{ ...defaultStyles.renderButtonText3 }}
        >
          {params.row.isProgressFilled ? (
            <Box
              component="img"
              src="/assets/svg/history.svg"
              sx={{ ...defaultStyles.imgStyle }}
            ></Box>
          ) : (
            <Box
              component="img"
              src="/assets/svg/history_red.svg"
              sx={{ ...defaultStyles.imgStyle }}
            ></Box>
          )}
        </Box>
      </>
    );
  };

  const tableColumns: any = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      sortable: false,
    },
    {
      field: "costEstimateID",
      headerName: "Cost Estimate ID",
      width: 185,
      sortable: false,
    },
    {
      field: "registrationNo",
      headerName: "Reg.No",
      width: 80,
      sortable: false,
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 150,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      sortable: true,
    },
    // {
    //   field: "division",
    //   headerName: "Division",
    //   width: 150,
    //   sortable: true,
    //   userType: ["INDIVIDUAL"],
    //   tab: [],
    // },
    {
      field: "allocateDivision",
      headerName: "Allocate Forest Division",
      width: 200,
      sortable: false,
      userType: ["INDIVIDUAL"],
      tab: [],
    },
    {
      field: "totalArea",
      headerName: "Net Planned Area(Ha)",
      width: 110,
      sortable: false,
    },
    {
      field: "pendingQueryCount",
      headerName: "Pending Progress Queries",
      width: 175,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {params.row.pendingQueryCount > 0 ? (
            <Box
              sx={{
                backgroundColor: "#ff5252",
                color: "white",
                borderRadius: "12px",
                padding: "4px 12px",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {params.row.pendingQueryCount}
            </Box>
          ) : (
            <Box sx={{ color: "#999", fontSize: "14px" }}>0</Box>
          )}
        </Box>
      ),
    },
    {
      field: "pendingSeedlingQueryCount",
      headerName: "Pending Seedling Queries",
      width: 175,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {params.row.pendingSeedlingQueryCount > 0 ? (
            <Box
              sx={{
                backgroundColor: "#ff5252",
                color: "white",
                borderRadius: "12px",
                padding: "4px 12px",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {params.row.pendingSeedlingQueryCount}
            </Box>
          ) : (
            <Box sx={{ color: "#999", fontSize: "14px" }}>0</Box>
          )}
        </Box>
      ),
    },
    {
      field: "ecoRestorationAmount",
      headerName: "Eco-Restoration Cost(₹)",
      width: 150,
      sortable: false,
    },
    {
      field: "totalFinancialProgress",
      headerName: "Total Financial Progress(₹)",
      width: 150,
      sortable: false,
    },

    {
      field: "digitalCostEstimate",
      headerName: "Digital Cost Estimate",
      width: 120,
      sortable: false,
      renderCell: RenderEPID,
    },
    {
      field: "viewProgressDetails",
      headerName: "Progress Details",
      width: 120,
      sortable: false,
      renderCell: ViewProgressDetails,
    },
  ];

  const exportToExcelEntity = async () => {
    setIsExporting(true);
    try {
      // Fetch all data with totalRecords as limit
      const response = await getProgressDetails({
        ...queryParams,
        offset: 0,
        limit: totalRecords || 10000, // Use total count or large number
        download: false,
      });

      const sortedData = response?.data?.data?.rows.sort((a, b) => {
        return b.isDigitalCostEstimateFilled - a.isDigitalCostEstimateFilled;
      });

      const allData = sortedData.map((row, idx) => ({
        sno: idx + 1,
        id: row?.id,
        costEstimateID: row.id,
        registrationNo: row.projectDetail.registration_no,
        entityName: row.user.firstName,
        state: row?.projectDetail?.address?.stateDetail?.name,
        division: row.projectDetail.landSpecification.division,
        allocateDivision:
          row.projectDetail.organizationAffiliationDetail?.userDetail?.division,
        totalArea: `${row.netPlantationArea}`,
        ecoRestorationAmount: formatINR(row?.demandNote ?? "-"),
        created_at: dateAndTimeStamp(row.created_at, "IST"),
        updated_at: dateAndTimeStamp(row.updated_at, "IST"),
        isProgressFilled: row.isProgressFilled,
        isDigitalCostEstimateFilled: row.isDigitalCostEstimateFilled,
        totalFinancialProgress: row.costProgress
          ? Number(row.costProgress).toFixed(2)
          : "0.00",
        totalSeedlings: row?.totalNumberOfSeedlingToBePlanted,
        pendingQueryCount: row?.pendingQueryCount || 0,
        pendingSeedlingQueryCount: row?.pendingSeedlingQueryCount || 0,
      }));

      const exportData = allData.map((item) => {
        return {
          "S.No": item?.sno,
          "Registration No": item?.registrationNo,
          "Entity Name": item.entityName,
          State: item?.state,
          "Allocate Division": item?.allocateDivision,
          Division: item?.division,
          "Net Planned Area (Ha)": item?.totalArea,
          "Pending Progress Queries": item?.pendingQueryCount,
          "Pending Seedling Queries": item?.pendingSeedlingQueryCount,
          "Eco-Restoration Cost (₹)": item?.ecoRestorationAmount,
          "Total Financial Progress (₹)": item?.totalFinancialProgress,
          "Total No. of Seedlings Proposed": item?.totalSeedlings,
          "Created At": item?.created_at,
          "Updated At": item?.updated_at,
        };
      });

      downloadSheet(exportData, generateExportFilename("progress-details"));
      setIsExporting(false);
    } catch (error) {
      console.error("Export failed:", error);
      setIsExporting(false);
    }
  };

  return (
    <DashboardWrapper
      title="Progress Details"
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={!!tableRows.length}
    >
      <Box>
        <Box sx={{}}>
          <Box sx={{ fontSize: "20px", fontWeight: "500" }}>
            {`Total digital cost estimate received:  ${totalCostEstimateReceived}`}
          </Box>

          <Box sx={{ fontSize: "20px", fontWeight: "500" }}>
            {`Total progress details received:  ${totalProgressDetailReceived}`}
          </Box>
        </Box>
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "2%",
            padding: "10px 1%",
            background: "white",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <SearchBar
            getText={handleSearch}
            defaultValue={searchDefaultValue}
            customStyles={{
              wrapper: {
                flex: 1,
                minWidth: "200px",
              },
            }}
          />
          <Box sx={{ display: "flex", gap: "8px" }}>
            <MultiSelect
              options={states}
              value={filterKey.stateIds}
              onChange={(e: any) =>
                handleFilterChange({ stateIds: e.length ? e : null })
              }
            />
          </Box>
          <MultiSelect
            options={entityInfo}
            label="Filter By Entity"
            clearable
            value={queryParams.entityIds ?? []}
            onChange={(e: string[]) =>
              handleFilterChange({ entityIds: e.length ? e : null })
            }
          />
          <Button
            onClick={exportToExcelEntity}
            text={"Export"}
            disabled={isExporting}
          />
        </Box>
        {/* Data Grid */}
        {isLoading || isFetching ? (
          <HomeTableSkeleton showTabs={false} columns={8} />
        ) : tableRows.length > 0 ? (
          <DataGrid
            rows={tableRows}
            columns={tableColumns}
            rowCount={totalRecords}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            onPageChange={(newPage) =>
              handlePaginationChange({ ...paginationModel, page: newPage })
            }
            onPageSizeChange={(newPageSize) =>
              handlePaginationChange({
                ...paginationModel,
                pageSize: newPageSize,
              })
            }
            rowsPerPageOptions={[20]}
            sortingMode="server"
            paginationMode="server"
            onSortModelChange={handleSort}
            columnBuffer={20}
            loading={isLoading || isFetching}
            getRowId={(row) => row.id}
          />
        ) : (
          <EmptyView
            heading="No Entry Found"
            subheading=""
            onClick={() => {}}
          />
        )}
      </Box>
    </DashboardWrapper>
  );
};

export default ProgressDetails;
